import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft } from 'lucide-react';

const ForgotPasswordForm = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const [redirectUrl, setRedirectUrl] = useState(null);

  useEffect(() => {
    // Automatically set the redirect URL based on the current origin
    setRedirectUrl(`${window.location.pathname}`);
  }, []);

  const validateEmail = (emailValue) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      setMessageType('error');
      setResetMessage('Please enter a valid email address');
      setTimeout(() => {
        setResetMessage('');
      }, 5000);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/auth/rest-pwd', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, redirectUrl }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessageType('success');
        setResetMessage(data.message || 'Password reset link sent to your email');
        setEmail('')
        setTimeout(() => {
          setResetMessage('');
        }, 15000);
      } else {
        setMessageType('error');
        setResetMessage(data.error || 'Failed to send verification code.');
        setTimeout(() => {
          setResetMessage('');
        }, 5000);
      }
    } catch (err) {
      setMessageType('error');
      setResetMessage('Failed to send verification code. Please try again.');
      setTimeout(() => {
        setResetMessage('');
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white">Reset Your Password</h2>
        <p className="text-gray-400">Enter your email to receive a reset link</p>
      </div>

      <form onSubmit={handleResetPassword} className="space-y-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail size={18} className="text-gray-400" />
          </div>
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full pl-10 px-4 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
          />
        </div>

        {resetMessage && (
          <div className={`text-center ${messageType === 'success' ? 'text-green-400' : 'text-red-400'}`}>
            {resetMessage}
          </div>
        )}

        <motion.button
          type="submit"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          disabled={isSubmitting}
          className="w-full py-2.5 bg-gradient-to-r from-teal-600 to-coral-600 text-white rounded-lg transition-all flex items-center justify-center space-x-2"
        >
          <Mail size={18} />
          <span>{isSubmitting ? 'Sending...' : 'Send Reset Link'}</span>
        </motion.button>
      </form>

      <div className="text-center text-sm flex justify-center">
        <button
          onClick={onBack}
          className="text-teal-400 hover:text-teal-300 flex items-center space-x-2"
        >
          <ArrowLeft size={16} />
          <span>Back to Sign In</span>
        </button>
      </div>
    </motion.div>
  );
};

export default ForgotPasswordForm;